import React from "react";
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  Button,
  Stack,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PageTitle from "../components/common/PageTitle";
import { useSubscriptions } from "../hooks/useSubscriptions";
import { getSubscriptionProducts } from "../utils/subscriptionProducts";

const MySubscriptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { subscriptions, loading, error } = useSubscriptions();

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="My Subscriptions"
        pageDescription="View all your product subscriptions. Each subscription can include multiple products with independent approval status."
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/add-subscription")}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Add Subscription
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              overflow: "hidden",
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                      Subscription name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                      Products
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                      Subscription status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptions.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        align="center"
                        sx={{
                          py: 4,
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? theme.palette.grey[300]
                              : theme.palette.text.secondary,
                          borderBottom: "none",
                        }}
                      >
                        <Typography variant="body2" component="span" sx={{ color: "inherit" }}>
                          You have no subscriptions yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    subscriptions.map((sub) => {
                      const products = getSubscriptionProducts(sub);
                      return (
                        <TableRow key={sub.uuid} hover>
                          <TableCell sx={{ color: "text.primary" }}>{sub.name}</TableCell>
                          <TableCell sx={{ color: "text.primary" }}>
                            {products.length === 0 ? (
                              "—"
                            ) : (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {products.map(({ product, productStatus }) => (
                                  <Chip
                                    key={product.uuid}
                                    label={product.name}
                                    size="small"
                                    color={productStatus === true ? "success" : "warning"}
                                    variant={productStatus === true ? "filled" : "outlined"}
                                    title={
                                      productStatus === true ? "Approved" : "Pending approval"
                                    }
                                  />
                                ))}
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={sub.status}
                              size="small"
                              color={sub.status === "ACTIVE" ? "success" : "default"}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default MySubscriptionsPage;
